// frontend/src/branding/theme.js

export const DEFAULT_THEME = {
  // Basic
  logo: null,
  primaryColor: "#ffb300",
  secondaryColor: "#111827",
  fontFamily: null,

  // Background gradient
  bgStart: "#093879",
  bgEnd: "#00d4ff",

  // Surfaces (optional - fallback to defaults in CSS)
  surface: null,
  surfaceDark: null,
  surfaceMuted: null,

  // Text (optional)
  text: null,
  textMuted: null,
  textInverse: null,

  // Semantic (optional)
  success: null,
  error: null,

  // Borders & overlays (optional)
  borderColor: null,
  overlayBg: null,

  // Derived (optional)
  primaryAlpha: null
};

function isString(x) {
  return typeof x === "string" && x.trim().length > 0;
}

function isProbablyCssColor(x) {
  if (!isString(x)) return false;
  const s = x.trim();
  return (
    s.startsWith("#") ||
    s.startsWith("rgb(") || s.startsWith("rgba(") ||
    s.startsWith("hsl(") || s.startsWith("hsla(") ||
    /^[a-zA-Z]+$/.test(s)
  );
}

function sanitizeTheme(maybeTheme) {
  const t = { ...DEFAULT_THEME };

  if (maybeTheme && typeof maybeTheme === "object") {
    // Basic
    if (isString(maybeTheme.logo)) t.logo = maybeTheme.logo.trim();
    if (isProbablyCssColor(maybeTheme.primaryColor)) t.primaryColor = maybeTheme.primaryColor.trim();
    if (isProbablyCssColor(maybeTheme.secondaryColor)) t.secondaryColor = maybeTheme.secondaryColor.trim();
    if (isString(maybeTheme.fontFamily)) t.fontFamily = maybeTheme.fontFamily.trim();

    // Background gradient
    if (isProbablyCssColor(maybeTheme.bgStart)) t.bgStart = maybeTheme.bgStart.trim();
    if (isProbablyCssColor(maybeTheme.bgEnd)) t.bgEnd = maybeTheme.bgEnd.trim();

    // Surfaces
    if (isProbablyCssColor(maybeTheme.surface)) t.surface = maybeTheme.surface.trim();
    if (isProbablyCssColor(maybeTheme.surfaceDark)) t.surfaceDark = maybeTheme.surfaceDark.trim();
    if (isProbablyCssColor(maybeTheme.surfaceMuted)) t.surfaceMuted = maybeTheme.surfaceMuted.trim();

    // Text
    if (isProbablyCssColor(maybeTheme.text)) t.text = maybeTheme.text.trim();
    if (isProbablyCssColor(maybeTheme.textMuted)) t.textMuted = maybeTheme.textMuted.trim();
    if (isProbablyCssColor(maybeTheme.textInverse)) t.textInverse = maybeTheme.textInverse.trim();

    // Semantic
    if (isProbablyCssColor(maybeTheme.success)) t.success = maybeTheme.success.trim();
    if (isProbablyCssColor(maybeTheme.error)) t.error = maybeTheme.error.trim();

    // Borders & overlays
    if (isProbablyCssColor(maybeTheme.borderColor)) t.borderColor = maybeTheme.borderColor.trim();
    if (isProbablyCssColor(maybeTheme.overlayBg)) t.overlayBg = maybeTheme.overlayBg.trim();

    // Derived
    if (isProbablyCssColor(maybeTheme.primaryAlpha)) t.primaryAlpha = maybeTheme.primaryAlpha.trim();

    // Legacy support: backgroundColor maps to surface
    if (isProbablyCssColor(maybeTheme.backgroundColor) && !t.surface) {
      t.surface = maybeTheme.backgroundColor.trim();
    }
  }

  return t;
}

/**
 * Apply theme to CSS custom properties.
 * Only sets properties that have values (preserves CSS defaults for nulls).
 */
export function applyThemeVars(theme) {
  const t = sanitizeTheme(theme);
  const r = document.documentElement;

  // Helper to set property only if value exists
  const setVar = (name, value) => {
    if (value) r.style.setProperty(name, value);
  };

  // Basic branding
  setVar("--brand-primary", t.primaryColor);
  setVar("--brand-secondary", t.secondaryColor);
  setVar("--app-font", t.fontFamily);

  // Background gradient
  setVar("--app-bg-start", t.bgStart);
  setVar("--app-bg-end", t.bgEnd);

  // Surfaces
  setVar("--surface", t.surface);
  setVar("--app-bg", t.surface); // Legacy alias
  setVar("--surface-dark", t.surfaceDark);
  setVar("--surface-muted", t.surfaceMuted);

  // Text
  setVar("--text", t.text);
  setVar("--text-muted", t.textMuted);
  setVar("--text-inverse", t.textInverse);

  // Semantic
  setVar("--success", t.success);
  setVar("--error", t.error);

  // Borders & overlays
  setVar("--border-color", t.borderColor);
  setVar("--overlay-bg", t.overlayBg);

  // Derived
  setVar("--brand-primary-alpha", t.primaryAlpha);

  // Expose for components to read
  window.__EXPO_THEME__ = t;

  return t;
}

function isValidThemeData(data) {
  if (!data || typeof data !== "object") return false;
  // Require at least primaryColor and secondaryColor
  return isString(data.primaryColor) && isString(data.secondaryColor);
}

export async function loadTheme() {
  let themeData = null;

  // 1) Try localStorage first
  try {
    const cached = localStorage.getItem("expo_theme");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (isValidThemeData(parsed)) {
        themeData = parsed;
      }
    }
  } catch (e) {
    console.warn("Could not load theme from localStorage:", e);
  }

  // 2) If no valid localStorage theme, fetch /theme.json
  if (!themeData) {
    try {
      const res = await fetch("/theme.json");
      if (res.ok) {
        const json = await res.json();
        if (isValidThemeData(json)) {
          themeData = json;
          try {
            localStorage.setItem("expo_theme", JSON.stringify(json));
          } catch (e) {
            // Ignore localStorage write errors
          }
        }
      }
    } catch (e) {
      console.warn("Could not load theme.json:", e);
    }
  }

  // 3) Fall back to DEFAULT_THEME
  if (!themeData) {
    themeData = DEFAULT_THEME;
  }

  return applyThemeVars(themeData);
}

export function saveTheme(theme) {
  const sanitized = sanitizeTheme(theme);

  try {
    localStorage.setItem("expo_theme", JSON.stringify(sanitized));
  } catch (e) {
    console.error("Could not save theme to localStorage:", e);
    throw e;
  }

  return applyThemeVars(sanitized);
}

export function resetTheme() {
  try {
    localStorage.removeItem("expo_theme");
  } catch (e) {
    console.warn("Could not clear theme from localStorage:", e);
  }

  return applyThemeVars(DEFAULT_THEME);
}

/**
 * Export theme as JSON string for download.
 */
export function exportThemeJson(theme) {
  const sanitized = sanitizeTheme(theme);
  // Remove null values for cleaner export
  const cleaned = Object.fromEntries(
    Object.entries(sanitized).filter(([, v]) => v !== null)
  );
  return JSON.stringify(cleaned, null, 2);
}

/**
 * Parse and validate imported theme JSON.
 * Returns sanitized theme or throws Error.
 */
export function importThemeJson(jsonString) {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    throw new Error("Invalid JSON format");
  }

  if (!isValidThemeData(parsed)) {
    throw new Error("Theme must include primaryColor and secondaryColor");
  }

  return sanitizeTheme(parsed);
}
